import { Search } from "@eai-toolkit-material-ui/icons";
import { DeleteOutline, Info, OpenInNew } from "@mui/icons-material";
import { Button } from "@mui/material";
import { getErrorMessage } from "@store/api/baseApi";
import { type ResourceType } from "@store/api/resourceTypes";
import {
  dehydrateRulesQueryState,
  dehydrateRulesQueryStateToString,
  hydrateRulesQueryState,
} from "@store/queryState";
import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  DEFAULT_CHIPS,
  LoadOverlay,
  QuickFilters,
  ViewWrapper,
} from "@components";
import { AccountSelector } from "@components/AccountSelector/AccountSelector";
import { ConfirmationDialog } from "@components/ConfirmationDialog/ConfirmationDialog";
import DescriptiveTooltip from "@components/DescriptiveTooltip";
import type { FilterName } from "@components/QuickFilters/QuickFilters";
import ResourceSelector, {
  type ResourceItem,
} from "@components/ResourceSelector/ResourceSelector";
import { SourceSelector } from "@components/SourceSelector/SourceSelector";
import {
  ActionButton,
  FilterBar,
  NoneFoundMessage,
  SearchRow,
} from "@components/ViewWrapper/styles";

import { getAccessRequestUrl } from "@helpers/constants";

import DataTable from "../../../components/DataTable/DataTable";
import { useDataTableUrlInit } from "../../../components/DataTable/useDataTableUrlInit";
import type { RowAction } from "../../../data/types";
import { useGetPermissionsListQuery } from "../../../store/api/permissionsApi";
import {
  useDeletePolicyMutation,
  useGetAllUserPolicyAccountsQuery,
  useGetAllUserResourceActionsQuery,
  useGetDeletablePolicyParentsQuery,
  useGetUserPoliciesQuery,
} from "../../../store/api/userRulesApi";
import {
  useGetMeQuery,
  useGetUserByIdQuery,
} from "../../../store/api/usersApi";
import { useAppDispatch } from "../../../store/hooks";
import { useMyMemberships } from "../../../store/hooks/useMyMemberships";
import { useQueryStateFromUrl } from "../../../store/hooks/useQueryStateFromUrl";
import { useTableControls } from "../../../store/hooks/useTableControls";
import { useUpdateQueryState } from "../../../store/hooks/useUpdateQueryState";
import { addSnackbarQueueItem } from "../../../store/slices/snackbarSlice";
import fieldRenderers from "./data/fieldRenderers";
import { defaultFieldLabels, fields, getDefaultSortOrder } from "./data/fields";
import { getResourceLink } from "./data/resourceLinks";
import {
  backendValuesToSource,
  deriveViewableResourceKeys,
  getHiddenPoliciesCount,
  type PermissionsSource,
  resourceKeyFromExpression,
  resourceUrnToExpressionAncestry,
  type Rule,
  sourceToBackendValues,
  userHasAllRequestableAccess,
} from "./data/types";
import ExpiringBanner from "./ExpiringBanner";
import RuleDetailDrawer from "./RuleDetailDrawer";
import ViewAsBanner from "./ViewAsBanner";

function ResourceColumnHeader() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      {/* Tooltip scoped to the label so it doesn't overlap the info icon's own. */}
      <DescriptiveTooltip title="Click to sort. Hold Shift and click to sort by multiple columns">
        <span>Resource</span>
      </DescriptiveTooltip>
      <DescriptiveTooltip title="Resources ending in * cascade to all descendants">
        <Info
          fontSize="small"
          sx={{ color: "action.active", cursor: "help" }}
          // Clicking the icon should reveal its tooltip, not trigger a sort.
          onClick={(e) => e.stopPropagation()}
        />
      </DescriptiveTooltip>
    </div>
  );
}

function isForbiddenError(err: unknown): boolean {
  return (
    !!err &&
    typeof err === "object" &&
    "status" in err &&
    (err as { status: unknown }).status === 403
  );
}

// Stable reference so ResourceSelector's memoized derivations (activeTypes,
// isTypeActive, the per-type query skip flags) aren't invalidated on every
// PermissionsTable render. Empty = search across all resource types.
const ALL_RESOURCE_TYPES: ResourceType[] = [];

const COLUMNS = [
  { ...fields.policyId, width: 118, visibleByDefault: false },
  { ...fields.action, width: 95, visibleByDefault: true },
  { ...fields.expression },
  { ...fields.scope },
  {
    ...fields.resource,
    visibleByDefault: true,
    customHeader: <ResourceColumnHeader />,
    contextActionsAlign: "right" as const,
  },
  { ...fields.cascades, visibleByDefault: false },
  { ...fields.source, visibleByDefault: true },
  { ...fields.created, visibleByDefault: true },
  { ...fields.expires, visibleByDefault: true },
  { ...fields.shareable },
].map((column) => ({
  ...column,
  label: defaultFieldLabels[column.fieldName],
  defaultSortOrder: getDefaultSortOrder(column),
}));

const PermissionsTable = () => {
  useDataTableUrlInit({
    tableId: "permissions-table",
    enableColumnSelection: true,
    allColumns: COLUMNS,
  });

  const { data: me, isLoading: isMeLoading } = useGetMeQuery();

  const queryState = useQueryStateFromUrl(hydrateRulesQueryState);
  const updateQueryState = useUpdateQueryState(dehydrateRulesQueryState);

  // View-as: null is the current user; otherwise the page reads another user's
  // permissions. All user-scoped read queries below use effectiveUserId.
  // URL-backed (see RulesQueryState.viewAsUserId) so it survives reloads and
  // shared links — the point of the feature is inspecting/sharing another
  // user's access.
  const viewAsUserId = queryState.viewAsUserId ?? null;
  const effectiveUserId = viewAsUserId ?? me?.id ?? "";
  const isViewingOther = !!viewAsUserId && !!me?.id && viewAsUserId !== me.id;
  const isExpiringSoonFilter = queryState.expiringSoon;
  const isExpiredFilter = queryState.expired;

  const queryKey = useMemo(
    () => dehydrateRulesQueryStateToString(queryState),
    [queryState],
  );

  const {
    currentData: response,
    isFetching: isPoliciesFetching,
    error,
  } = useGetUserPoliciesQuery(
    {
      queryKey,
      userId: effectiveUserId,
      expiringSoon: isExpiringSoonFilter,
      expired: isExpiredFilter,
    },
    { skip: !effectiveUserId },
  );

  const isLoading = isMeLoading || isPoliciesFetching || !effectiveUserId;

  // All accounts that the user has policies in — drives the Account filter options.
  const {
    data: accounts = [],
    isLoading: areAccountsLoading,
    isFetching: areAccountsFetching,
  } = useGetAllUserPolicyAccountsQuery(
    { userId: effectiveUserId },
    { skip: !effectiveUserId },
  );

  // The admin badge asserts something about the signed-in user, so in view-as (where
  // the accounts are not the signed-in user's) the flag is dropped.
  const accountOptions = useMemo(
    () =>
      isViewingOther
        ? accounts.map(({ id, name }) => ({ id, name })) // isAdmin intentionally not included
        : accounts,
    [accounts, isViewingOther],
  );

  // Total count of policies expiring within EXPIRING_SOON_DAYS — drives the banner.
  // Uses the same query as the quick filter (expiringSoon: true, no other filters)
  // so the result is cached for if the user later enables that filter.
  const { data: expiringSoonList } = useGetUserPoliciesQuery(
    { queryKey: "", userId: effectiveUserId, expiringSoon: true },
    { skip: !effectiveUserId },
  );
  const expiringSoonCount = expiringSoonList?.metadata?.total ?? 0;

  // The gating below is keyed by the effective user's urn + fullName. The me
  // object already carries those fields; the other user is known here only by id,
  // so fetch their record to get them. currentData (not data): data lags an arg
  // change, so on a switch between two users it would briefly return the previous
  // id's record; currentData is undefined until this id resolves, keeping the
  // gating fail-closed mid-switch rather than confidently-stale.
  const { currentData: targetUser } = useGetUserByIdQuery(
    { id: effectiveUserId },
    { skip: !isViewingOther || !effectiveUserId },
  );
  const effectiveUser = isViewingOther ? targetUser : me;

  // Each row's delete is gated on the grant's parent — the effective user, or a
  // role/team the grant is inherited through. The parents come from the effective
  // user, but the gate checks whether the *current* user holds policy:rm on them,
  // mirroring the backend's delete check. So in view-as we're checking the
  // current user's rights over the viewed user's parents, not the viewed user's.
  const { memberships, teams } = useMyMemberships(effectiveUserId);
  const policyParents = useMemo(() => {
    if (!effectiveUser?.urn || !effectiveUser.fullName) return [];
    const byUrn = new Map<string, { fullName: string; urn: string }>();
    byUrn.set(effectiveUser.urn, {
      fullName: effectiveUser.fullName,
      urn: effectiveUser.urn,
    });
    for (const m of memberships) {
      if (m.urn && m.fullName) {
        byUrn.set(m.urn, { fullName: m.fullName, urn: m.urn });
      }
    }
    // Sort for a stable query cache key.
    return [...byUrn.values()].sort((a, b) => a.urn.localeCompare(b.urn));
  }, [effectiveUser?.urn, effectiveUser?.fullName, memberships]);
  // Map of parent full name → whether the user may delete policies on it.
  const { data: canDeleteByParent = {} } = useGetDeletablePolicyParentsQuery(
    { parents: policyParents },
    { skip: policyParents.length === 0 },
  );

  const dispatch = useAppDispatch();
  const [deletePolicy] = useDeletePolicyMutation();
  const [policyToDelete, setPolicyToDelete] = useState<Rule | null>(null);
  // The rule whose detail drawer is open (also drives the row highlight). Null
  // when the drawer is closed.
  const [selectedRule, setSelectedRule] = useState<Rule | null>(null);

  const [columnsButton, setColumnsButton] = useState<React.ReactNode>(null);
  const handleColumnsButtonRender = useCallback(
    (button: React.ReactNode) => setColumnsButton(button),
    [],
  );

  const { handleSortChange, handlePrevPage, handleNextPage } =
    useTableControls<Rule>({
      queryState,
      updateQueryState: (newState) =>
        updateQueryState({ ...queryState, ...newState }),
    });

  const data = useMemo(() => response?.items ?? [], [response]);
  const totalItems = response?.metadata?.total ?? 0;

  const QUICK_FILTERS = [DEFAULT_CHIPS.isExpiringSoon, DEFAULT_CHIPS.isExpired];

  const onSetQuickFilter = useCallback(
    ({
      filterName,
      isSelected,
    }: {
      filterName: FilterName;
      isSelected: boolean;
    }) => {
      if (filterName === "isExpiringSoon") {
        updateQueryState({
          ...queryState,
          expiringSoon: isSelected,
          expired: false,
          paging: { ...queryState.paging, offset: 0 },
        });
      } else if (filterName === "isExpired") {
        updateQueryState({
          ...queryState,
          expired: isSelected,
          expiringSoon: false,
          paging: { ...queryState.paging, offset: 0 },
        });
      }
    },
    [queryState, updateQueryState],
  );

  // In self-view, derive which resources the user can open from their `get`
  // grants. In view-as, the backend only returns resource-specific policies when
  // the signed-in viewer already has `get` access to the resource.
  const viewableResourceKeys = useMemo(
    () => deriveViewableResourceKeys(data),
    [data],
  );

  const canViewResource = useCallback(
    (row: Rule) => {
      if (isViewingOther) return true;
      const key = resourceKeyFromExpression(row.expression);
      return !!key && viewableResourceKeys.has(key);
    },
    [isViewingOther, viewableResourceKeys],
  );

  const openResourceAction = useMemo<RowAction<Rule>>(
    () => ({
      label: "Go to resource",
      getAction(row: Rule) {
        const link = getResourceLink(row);
        if (!link || !canViewResource(row)) return null;
        return { externalLink: link };
      },
      hidden: (row: Rule) => !getResourceLink(row) || !canViewResource(row),
      icon: () => <OpenInNew fontSize="small" color="primary" />,
    }),
    [canViewResource],
  );

  // The Resource column's trailing "Go to resource" action depends on per-resource
  // view permission, so it's attached here rather than on the static COLUMNS.
  const columns = useMemo(
    () =>
      COLUMNS.map((column) =>
        column.fieldName === "resource"
          ? { ...column, contextActions: [openResourceAction] }
          : column,
      ),
    [openResourceAction],
  );

  const paging = useMemo(
    () => ({
      ...queryState.paging,
      total: {
        count: totalItems,
        isCountFinal: true,
      },
    }),
    [queryState.paging, totalItems],
  );

  // Clicking a row opens its detail drawer. The row's own action buttons
  // (delete, "Go to resource") stop propagation, so they don't also open it.
  const handleRowClick = useCallback((row: Rule) => {
    setSelectedRule(row);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!policyToDelete || !effectiveUserId) return;
    // userId is only used to delete a direct grant, whose endpoint is scoped to a
    // user (DELETE /v1/user/{userId}/policy/…). Passing effectiveUserId means a
    // direct delete removes the grant from the viewed user in view-as. Team/role
    // grants ignore userId and route by source.id.
    deletePolicy({
      policyId: policyToDelete.policyId,
      source: policyToDelete.source,
      userId: effectiveUserId,
    })
      .unwrap()
      .catch((err) => {
        dispatch(
          addSnackbarQueueItem({
            message: `Failed to delete policy: ${getErrorMessage(err) ?? "Unknown error"}`,
            variant: "error",
          }),
        );
      });
    setPolicyToDelete(null);
  }, [deletePolicy, dispatch, policyToDelete, effectiveUserId]);

  const deletePolicyAction = useMemo<RowAction<Rule>>(
    () => ({
      label: "Delete policy",
      getAction(row: Rule) {
        // Blocked when the user can't manage the row's parent (keyed by source.id).
        const blocked = !canDeleteByParent[row.source.id ?? ""];
        return {
          onClick: blocked ? undefined : () => setPolicyToDelete(row),
          disabled: blocked,
          disabledTooltip: blocked
            ? "You don't have permission to delete this policy"
            : undefined,
        };
      },
      hidden: (row: Rule) => row.source.kind === "unknown",
      icon: (disabled: boolean) => (
        <DeleteOutline
          fontSize="small"
          color={disabled ? "disabled" : "error"}
        />
      ),
    }),
    [canDeleteByParent],
  );

  const rowActions = useMemo(() => [deletePolicyAction], [deletePolicyAction]);

  // URL-backed (see RulesQueryState.selectedResource), so it survives reloads
  // and shared links like the other filters.
  const selectedResource = queryState.selectedResource ?? null;

  const selectedResourceForRequest = useMemo(
    () => selectedResource?.id ?? null,
    [selectedResource],
  );

  // Full action→permissions catalog (every type): a grant shown here may come
  // from a cascading account-level action whose permissions we still resolve.
  const { data: permissionsList, isLoading: isPermissionsLoading } =
    useGetPermissionsListQuery();
  const allMappings = useMemo(
    () => permissionsList?.items ?? [],
    [permissionsList],
  );

  // Held actions for the selected resource across all pages, so the check below
  // isn't fooled by pagination.
  const resourceExpressionValues = useMemo(
    () =>
      selectedResource?.urn
        ? resourceUrnToExpressionAncestry(selectedResource.urn)
        : [],
    [selectedResource],
  );
  const {
    data: heldResourceActions = [],
    isLoading: isHeldLoading,
    isFetching: isHeldFetching,
  } = useGetAllUserResourceActionsQuery(
    { userId: effectiveUserId, expressionValues: resourceExpressionValues },
    { skip: !effectiveUserId || resourceExpressionValues.length === 0 },
  );

  // The decision below depends on the catalog and the held actions; both default
  // to empty while loading, which would read as "holds nothing" and briefly show
  // the button before it resolves to hidden (a flash). Wait until they've settled.
  // The held-actions query only runs when the resource has an ancestry to check.
  const requestAccessDataPending =
    isPermissionsLoading ||
    (resourceExpressionValues.length > 0 && (isHeldLoading || isHeldFetching));

  // Show "Request access" only when a resource is selected and something's left
  // to request (see userHasAllRequestableAccess).
  const showRequestAccess = useMemo(
    () =>
      !isViewingOther &&
      !!selectedResource &&
      resourceExpressionValues.length > 0 &&
      !requestAccessDataPending &&
      !userHasAllRequestableAccess(
        allMappings,
        selectedResource.resourceType,
        heldResourceActions,
      ),
    [
      isViewingOther,
      selectedResource,
      resourceExpressionValues,
      requestAccessDataPending,
      allMappings,
      heldResourceActions,
    ],
  );

  const sourceValue = backendValuesToSource(
    queryState.query.filters.get("source")?.valueList ?? [],
  );

  const handleSourceChange = useCallback(
    (value: PermissionsSource) => {
      const newFilters = new Map(queryState.query.filters);
      const backendValues = sourceToBackendValues(value);
      if (backendValues.length > 0) {
        newFilters.set("source", {
          valueList: backendValues,
          booleanValueList: [],
          fuzzyMatch: null,
          range: [],
        });
      } else {
        newFilters.delete("source");
      }
      updateQueryState({
        ...queryState,
        query: { ...queryState.query, filters: newFilters },
        paging: { ...queryState.paging, offset: 0 },
      });
    },
    [queryState, updateQueryState],
  );

  // Account and resource filters both narrow the `expression` field (account =
  // fuzzy id, resource = ancestry list), so only one is active at a time;
  // selecting one clears the other.
  const accountValue =
    queryState.query.filters.get("expression")?.fuzzyMatch ?? "";

  const handleAccountChange = useCallback(
    (accountId: string | null) => {
      // Shared filter — picking an account clears the resource selection.
      const newFilters = new Map(queryState.query.filters);
      if (accountId) {
        newFilters.set("expression", {
          valueList: [],
          booleanValueList: [],
          fuzzyMatch: accountId,
          range: [],
        });
      } else {
        newFilters.delete("expression");
      }
      updateQueryState({
        ...queryState,
        selectedResource: null,
        query: { ...queryState.query, filters: newFilters },
        paging: { ...queryState.paging, offset: 0 },
      });
    },
    [queryState, updateQueryState],
  );

  // Match the resource URN and each ancestor level so cascading grants made
  // higher up are included (see resourceUrnToExpressionAncestry).
  const handleResourceChange = useCallback(
    (resource: ResourceItem | null) => {
      const newFilters = new Map(queryState.query.filters);
      const ancestry = resource?.urn
        ? resourceUrnToExpressionAncestry(resource.urn)
        : [];
      if (ancestry.length > 0) {
        newFilters.set("expression", {
          valueList: ancestry,
          booleanValueList: [],
          fuzzyMatch: null,
          range: [],
        });
      } else {
        newFilters.delete("expression");
      }
      updateQueryState({
        ...queryState,
        selectedResource: resource,
        query: { ...queryState.query, filters: newFilters },
        paging: { ...queryState.paging, offset: 0 },
      });
    },
    [queryState, updateQueryState],
  );

  // Switching the viewed user resets paging (page N is meaningless against a
  // different list) and clears the account/resource filter, which is scoped to
  // the previous user's grants — keeping it would silently empty the table.
  const handleViewAsChange = useCallback(
    (userId: string | null) => {
      const newFilters = new Map(queryState.query.filters);
      newFilters.delete("expression");
      updateQueryState({
        ...queryState,
        viewAsUserId: userId,
        selectedResource: null,
        query: { ...queryState.query, filters: newFilters },
        paging: { ...queryState.paging, offset: 0 },
      });
    },
    [queryState, updateQueryState],
  );

  return (
    <ViewWrapper
      docsSection="PERMISSIONS"
      title="Permissions"
      topContent={
        <>
          <ViewAsBanner
            value={viewAsUserId}
            onChange={handleViewAsChange}
            isViewingOther={isViewingOther}
            effectiveUserName={
              isViewingOther ? effectiveUser?.fullName : undefined
            }
            hiddenPoliciesCount={getHiddenPoliciesCount(response)}
            isPoliciesFetching={isPoliciesFetching}
          />
          <ExpiringBanner
            count={expiringSoonCount}
            onShowExpiring={() =>
              updateQueryState({
                ...queryState,
                expiringSoon: true,
                expired: false,
                // Narrowing the list invalidates the current page — page N of
                // the unfiltered list can be past the end of the filtered one,
                // which would land the user on an empty table.
                paging: { ...queryState.paging, offset: 0 },
              })
            }
            isFilterActive={isExpiringSoonFilter}
          />
        </>
      }
      aboveContent={
        <FilterBar>
          <AccountSelector
            value={accountValue || null}
            onChange={handleAccountChange}
            options={accountOptions}
            loading={areAccountsLoading || areAccountsFetching}
          />
          <SourceSelector value={sourceValue} onChange={handleSourceChange} />
        </FilterBar>
      }
    >
      <SearchRow>
        <ResourceSelector
          resourceType={ALL_RESOURCE_TYPES}
          value={selectedResource}
          onChange={handleResourceChange}
          startIcon={
            <Search
              color="secondary"
              sx={(theme) => ({ ml: 2, mr: 2, fontSize: theme.spacing(2) })}
            />
          }
          label=""
          ariaLabel="Search by resource"
          placeholder="Search by resource"
          // Restyle to match the other search bars on list pages (e.g. Jobs)
          sx={(theme) => ({
            flexGrow: 1,
            "& .MuiOutlinedInput-notchedOutline": { border: "none" },
            "& .MuiAutocomplete-inputRoot": {
              backgroundColor: theme.palette.common.white,
              borderRadius: theme.spacing(0.5),
              padding: theme.spacing(1),
              // Reserve room on the right for the absolutely-positioned clear (x) button.
              paddingRight: theme.spacing(5),
            },
            "& .MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
              padding: "4px 0 5px",
            },
            "& .MuiAutocomplete-popupIndicator": { display: "none" },
            "& .MuiAutocomplete-clearIndicator": {
              visibility: "visible",
              color: theme.palette.secondary.main,
            },
          })}
          metadataFallback
        />
        {showRequestAccess && (
          <ActionButton
            color="primary"
            variant="contained"
            component={Link}
            // Carry the resource id so the request form opens pre-populated;
            // blank when nothing is selected.
            to={getAccessRequestUrl(selectedResourceForRequest)}
            state={{ canGoBack: true }}
          >
            Request access
          </ActionButton>
        )}
      </SearchRow>
      <QuickFilters
        filters={QUICK_FILTERS}
        filterState={{
          isExpiringSoon: isExpiringSoonFilter,
          isExpired: isExpiredFilter,
        }}
        setQuickFilter={({ filterName, isSelected }) =>
          onSetQuickFilter({ filterName, isSelected })
        }
        columnsButton={columnsButton}
      />
      <LoadOverlay loading={isLoading} />
      {!isLoading && !error && data.length === 0 && (
        <NoneFoundMessage>
          No permissions found for this query. Try adjusting your filters.
        </NoneFoundMessage>
      )}
      {error && isForbiddenError(error) && isViewingOther ? (
        // The policy list itself never 403s (it filters instead), but adjacent
        // calls in view-as can. Offer an escape back to self-view instead of a
        // generic error with no way out.
        <NoneFoundMessage>
          <span role="alert">
            You don&apos;t have permission to view{" "}
            {effectiveUser?.fullName ?? "this user"}&apos;s permissions.
          </span>{" "}
          <Button size="small" onClick={() => handleViewAsChange(null)}>
            View as me
          </Button>
        </NoneFoundMessage>
      ) : (
        error && (
          <p role="alert" style={{ color: "red" }}>
            Error: Failed to load permissions
          </p>
        )
      )}
      {!isLoading && !error && data.length > 0 && (
        <DataTable
          ariaLabel="Permissions table"
          columns={columns}
          data={data}
          fieldRenderers={fieldRenderers}
          sort={queryState.query.sort}
          paging={paging}
          setSort={handleSortChange}
          onPrevPageClick={handlePrevPage}
          onNextPageClick={handleNextPage}
          onRowClick={handleRowClick}
          highlightedRowId={selectedRule?.id}
          actions={rowActions}
          inlineActions={true}
          enableColumnSelection={true}
          tableId="permissions-table"
          renderColumnsButtonExternally={true}
          onRenderColumnsButton={handleColumnsButtonRender}
        />
      )}
      <ConfirmationDialog
        open={!!policyToDelete}
        title="Delete policy"
        message={
          policyToDelete && (
            <>
              Are you sure you want to delete{" "}
              {isViewingOther ? (
                <>
                  <strong>{effectiveUser?.fullName ?? "this user"}</strong>
                  &apos;s{" "}
                </>
              ) : (
                "the "
              )}
              <strong>{policyToDelete.action}</strong> grant on{" "}
              <strong>
                {policyToDelete.resource.name ?? policyToDelete.expression}
              </strong>
              ? This action cannot be undone.
              {(policyToDelete.source.kind === "team" ||
                policyToDelete.source.kind === "role") &&
                ` This policy is inherited from a ${policyToDelete.source.kind}; removing it affects everyone who gets it that way.`}
              {/* A policy can appear as more than one row, so one delete may
                  remove several. It's not worth it to count them (copies can sit on
                  other pages), so warn generally rather than cite a number. */}
              {
                " Deleting this policy may remove more than one row from the list."
              }
            </>
          )
        }
        confirmText="Delete"
        confirmColor="error"
        onConfirm={handleConfirmDelete}
        onCancel={() => setPolicyToDelete(null)}
      />
      <RuleDetailDrawer
        rule={selectedRule}
        onClose={() => setSelectedRule(null)}
        canViewResource={selectedRule ? canViewResource(selectedRule) : false}
        userLabel={
          isViewingOther
            ? effectiveUser?.fullName || effectiveUser?.name || effectiveUserId
            : "you"
        }
        canRequestExtension={!isViewingOther}
        // For resolving which team a role:team grant arrived through.
        userTeams={teams}
      />
    </ViewWrapper>
  );
};

export default PermissionsTable;
